import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

import type { MessageItemProps } from '@ergeon/core-components';

import { createAIAgentThread, getAIAgentMessages, sendAIAgentMessage, deleteAIAgentThread, getAIAgentThreads } from '../api';
import type { AIAgentThread } from '../types';

import { usePagination } from './usePagination';
import { useAIAgentCentrifugo } from './useAIAgentCentrifugo';
import { useAIAgentPolling } from './useAIAgentPolling';
import { useAIAgentMessages } from './useAIAgentMessages';

const STORAGE_KEY = 'ai-agent-thread-id';

export const useAIAgent = () => {
  const [text, setText] = useState('');
  const [messages, setMessages] = useState<MessageItemProps[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentThreadId, setCurrentThreadId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentThreadIdRef = useRef<number | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  // Thread history pagination
  const fetchThreadsPage = useCallback(async(page: number) => getAIAgentThreads({ page, page_size: 20 }), []);
  const threadsPagination = usePagination<AIAgentThread>({
    fetchPage: fetchThreadsPage,
    initialPage: 1,
  });
  const refetchThreads = threadsPagination.refetch;
  const removeThread = threadsPagination.removeItem;

  // Message conversion
  const { convertToMessageItems } = useAIAgentMessages();

  // Polling callbacks
  const pollingCallbacks = useMemo(() => ({
    onMessagesReceived: (apiMessages: Parameters<typeof convertToMessageItems>[0]) => {
      setMessages(convertToMessageItems([...apiMessages].reverse()));
    },
    onAssistantResponse: () => {
      refetchThreads();
    },
    onError: (message: string) => {
      setError(message);
    },
    onComplete: () => {
      setIsSubmitting(false);
    },
  }), [convertToMessageItems, refetchThreads]);

  // Polling
  const { startPolling, stopPolling, setLastMessageId } = useAIAgentPolling(pollingCallbacks);

  // Fetch messages for a thread
  const fetchMessages = useCallback(async(threadId: number) => {
    try {
      const messagesResponse = await getAIAgentMessages(threadId);
      console.info('[AIAgent] Fetched messages:', messagesResponse);

      if (messagesResponse.results.length > 0) {
        setMessages(convertToMessageItems([...messagesResponse.results].reverse()));
        setLastMessageId(messagesResponse.results[0].id);

        // Check if we have an assistant response
        const hasAssistantResponse = messagesResponse.results.some((msg) => msg.role === 'assistant');
        if (hasAssistantResponse) {
          setIsSubmitting(false);
          stopPolling();
          refetchThreads();
        }
      }
    } catch (err) {
      console.error('[AIAgent] Error fetching messages:', err);
    }
  }, [convertToMessageItems, setLastMessageId, stopPolling, refetchThreads]);

  // Centrifugo handler
  const handleAgentResponse = useCallback((notificationThreadId: number, agentError?: string | null) => {
    console.info('[AIAgent] Received Centrifugo notification for thread:', notificationThreadId, 'error:', agentError);

    if (agentError) {
      setError(`AI Agent error: ${agentError}`);
      setIsSubmitting(false);
      return;
    }

    // Fetch latest messages when we get a notification for our thread
    if (currentThreadIdRef.current === notificationThreadId) {
      fetchMessages(notificationThreadId);
    }
  }, [fetchMessages]);

  // Centrifugo subscription
  useAIAgentCentrifugo(handleAgentResponse);

  // Load existing thread from localStorage on mount
  useEffect(() => {
    const loadThread = async() => {
      try {
        const savedThreadId = localStorage.getItem(STORAGE_KEY);
        console.info('[AIAgent] Loading thread from localStorage:', savedThreadId);

        if (savedThreadId) {
          const threadId = parseInt(savedThreadId, 10);
          console.info('[AIAgent] Fetching messages for thread:', threadId);
          const messagesResponse = await getAIAgentMessages(threadId);
          console.info('[AIAgent] Loaded messages:', messagesResponse);

          if (messagesResponse.results.length > 0) {
            setCurrentThreadId(threadId);
            setMessages(convertToMessageItems([...messagesResponse.results].reverse()));
            setLastMessageId(messagesResponse.results[0].id);
            console.info('[AIAgent] Thread restored with', messagesResponse.results.length, 'messages');

            // Check if last message is from user (thread is still processing)
            const lastMessage = messagesResponse.results[0];
            if (lastMessage.role === 'user') {
              console.info('[AIAgent] Thread is still processing, waiting for Centrifugo notification (with polling fallback)...');
              setIsSubmitting(true);
              startPolling(threadId);
            }
          }
        } else {
          console.info('[AIAgent] No saved thread found');
        }
      } catch (err) {
        console.error('[AIAgent] Error loading thread:', err);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    loadThread();
  }, [convertToMessageItems, setLastMessageId, startPolling]);

  // Save thread ID to localStorage when it changes
  useEffect(() => {
    if (currentThreadId !== null) {
      console.info('[AIAgent] Saving thread_id to localStorage:', currentThreadId);
      localStorage.setItem(STORAGE_KEY, currentThreadId.toString());
    }
  }, [currentThreadId]);

  // Select a thread from history
  const selectThread = useCallback(async(threadId: number) => {
    if (threadId === currentThreadId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const messagesResponse = await getAIAgentMessages(threadId);

      if (messagesResponse.results.length > 0) {
        setCurrentThreadId(threadId);
        setMessages(convertToMessageItems([...messagesResponse.results].reverse()));
        setLastMessageId(messagesResponse.results[0].id);
        localStorage.setItem(STORAGE_KEY, threadId.toString());
        console.info('[AIAgent] Switched to thread:', threadId);

        // Check if thread is still processing
        const lastMessage = messagesResponse.results[0];
        if (lastMessage.role === 'user') {
          setIsSubmitting(true);
          startPolling(threadId);
        }
      }
    } catch (err) {
      console.error('[AIAgent] Error loading thread:', err);
      setError('Failed to load conversation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [convertToMessageItems, currentThreadId, setLastMessageId, startPolling]);

  const startNewConversation = useCallback(() => {
    setCurrentThreadId(null);
    setMessages([]);
    setText('');
    setError(null);
    setIsSubmitting(false);
    setLastMessageId(null);
    stopPolling();
    localStorage.removeItem(STORAGE_KEY);
    console.info('[AIAgent] Started new conversation');
  }, [setLastMessageId, stopPolling]);

  // Delete a thread (optimistic update)
  const handleDeleteThread = useCallback(async(threadId: number): Promise<void> => {
    const isCurrentThread = currentThreadIdRef.current !== null &&
      Number(threadId) === Number(currentThreadIdRef.current);

    // Optimistic update: immediately remove from UI
    removeThread((thread) => thread.id === threadId);

    // If deleting the current thread, clear the messages immediately
    if (isCurrentThread) {
      startNewConversation();
    }

    // Make API call
    try {
      await deleteAIAgentThread(threadId);
      console.info('[AIAgent] Thread deleted successfully:', threadId);
    } catch (err) {
      console.error('[AIAgent] Error deleting thread:', err);
      setError('Failed to delete conversation. Please try again.');

      // Refetch to restore thread list on error
      await refetchThreads();

      // Restore selection if it was the current thread
      if (isCurrentThread) {
        setCurrentThreadId(threadId);
        // Reload messages for the restored thread
        try {
          const messagesResponse = await getAIAgentMessages(threadId);
          if (messagesResponse.results.length > 0) {
            setMessages(convertToMessageItems([...messagesResponse.results].reverse()));
            localStorage.setItem(STORAGE_KEY, threadId.toString());
          }
        } catch {
          // If we can't restore messages, just leave it in new conversation state
        }
      }

      throw err; // Re-throw so the UI knows the delete failed
    }
  }, [startNewConversation, removeThread, refetchThreads, convertToMessageItems]);

  const handleSubmit = useCallback(async() => {
    if (!text.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    stopPolling();

    const messageContent = text.trim();
    setText('');

    try {
      if (currentThreadId === null) {
        // Create new thread with first message
        console.info('[AIAgent] Creating new thread');
        const newMessage = await createAIAgentThread(messageContent);
        console.info('[AIAgent] Thread created, id:', newMessage.thread_id);

        setCurrentThreadId(newMessage.thread_id);
        setLastMessageId(newMessage.id);

        // Show user message immediately
        setMessages([{
          content: messageContent,
          delivered_at: newMessage.created_at,
          alignment: 'end',
          sender: 'You',
          meta: 'Question',
        }]);

        refetchThreads();
        console.info('[AIAgent] Waiting for response via Centrifugo (polling fallback enabled)');
        startPolling(newMessage.thread_id);
      } else {
        // Send message to existing thread
        console.info('[AIAgent] Sending message to thread:', currentThreadId);
        const newMessage = await sendAIAgentMessage(currentThreadId, messageContent);

        setLastMessageId(newMessage.id);

        // Add user message to messages
        setMessages((prev) => [...prev, {
          content: messageContent,
          delivered_at: newMessage.created_at,
          alignment: 'end',
          sender: 'You',
          meta: 'Question',
        }]);

        console.info('[AIAgent] Waiting for response via Centrifugo (polling fallback enabled)');
        startPolling(currentThreadId);
      }
    } catch (err) {
      console.error('[AIAgent] Error submitting message:', err);
      setError('Failed to send message. Please try again.');
      setIsSubmitting(false);
      setText(messageContent);
    }
  }, [text, currentThreadId, setLastMessageId, stopPolling, startPolling, refetchThreads]);

  const isDisabled = isSubmitting || !text.trim();

  return {
    // State
    text,
    setText,
    messages,
    isSubmitting,
    isLoading,
    currentThreadId,
    error,
    threads: threadsPagination.data,
    isLoadingThreads: threadsPagination.isLoading,
    hasMoreThreads: threadsPagination.hasMore,
    isDisabled,
    scrollContainerRef,

    // Actions
    loadMoreThreads: threadsPagination.loadMore,
    selectThread,
    startNewConversation,
    handleDeleteThread,
    handleSubmit,
  };
};

export default useAIAgent;
